'use client';

/**
 * DialogLocksAuth
 *
 * Modal that sets a creator up for Locks (Intro → Enable Locks → Enable Payments → Enabled):
 *  1. Intro          — explains Locks; "Continue" begins the flow.
 *  2. Enable Locks   — iframe loads the Lock Server `/connect` page. On approval it posts the code
 *                      to the parent and `useLocksAuthFlow` exchanges it.
 *  2b. Enable Locks  — only for a session from before the app requested `/priv` (#2373): Pubky Ring
 *                      approves a session with today's capability list and it replaces the stored one.
 *                      Steps 2 and 2b are numbered (1/2) and (2/2) when both are pending at open.
 *  3. Enable Payments — iframe loads Paykit's `/setup` page, where the creator connects the account
 *                      that receives payments. The Lock Server is asked first whether this creator's
 *                      Paykit setup is already done (#2627): `ready` skips the step, and `unavailable`
 *                      offers a retry instead of the page.
 *  4. Enabled        — success; "Continue" notifies the caller via `onSuccess` and closes.
 *
 * Which step shows is derived from the stores, so a creator who is already signed in, but whose
 * Paykit setup this Locks session has not confirmed yet, opens straight at Enable Payments.
 */
import { type ReactNode, useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { Button, ButtonVariant } from '@/atoms/Button/Button';
import { Container } from '@/atoms/Container/Container';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/atoms/Dialog/Dialog';
import { Image } from '@/atoms/Image/Image';
import { Link } from '@/atoms/Link/Link';
import { Typography } from '@/atoms/Typography/Typography';
import {
  BITKIT_APP_STORE_URL,
  BITKIT_PLAY_STORE_URL,
  BITKIT_WEBSITE_URL,
  getAppStoreLink,
  getPlayStoreLink,
  getPubkyRingLink,
} from '@/config/externalLinks';
import { useLocksAuthFlow } from '@/hooks/useLocksAuthFlow/useLocksAuthFlow';
import { LocksAuthFlowStatus } from '@/hooks/useLocksAuthFlow/useLocksAuthFlow.types';
import { usePaykitSetupFlow } from '@/hooks/usePaykitSetupFlow/usePaykitSetupFlow';
import { PaykitSetupFlowStatus } from '@/hooks/usePaykitSetupFlow/usePaykitSetupFlow.types';
import { useSessionNeedsUpgrade } from '@/hooks/useSessionNeedsUpgrade/useSessionNeedsUpgrade';
import { cn } from '@/libs/utils/utils';
import { AppDownload } from '@/molecules/AppDownload/AppDownload';
import { SessionUpgradeDescription, SessionUpgradePanel } from '@/organisms/SessionUpgradePanel/SessionUpgradePanel';
import { isLocksAuthenticated as isLocksAuthenticatedState } from '@/stores/locksAuth/locksAuth.selectors';
import { useLocksAuthStore } from '@/stores/locksAuth/locksAuth.store';
import {
  focusLocksDialogPanel,
  LOCKS_DIALOG_CARD_CLASSNAME,
  LOCKS_DIALOG_DESCRIPTION_TRACKING,
} from './DialogLocksAuth.constants';

type DialogLocksAuthProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

// Frames the 192px step illustration.
function StepImage({ src, alt }: { src: string; alt: string }) {
  return (
    <Container className="flex items-center justify-center px-12 py-6">
      <Image src={src} alt={alt} width={192} height={192} className="size-48" />
    </Container>
  );
}

export function DialogLocksAuth({ open, onOpenChange, onSuccess }: DialogLocksAuthProps) {
  const {
    status: locksStatus,
    connectUrl,
    error: locksError,
    iframeRef: locksIframeRef,
    prepare,
    start: startLocks,
    reset: resetLocks,
  } = useLocksAuthFlow();
  const {
    status: paykitStatus,
    setupUrl,
    error: paykitError,
    iframeRef: paykitIframeRef,
    check: checkPaykit,
    reset: resetPaykit,
  } = usePaykitSetupFlow();
  const isLocksAuthenticated = isLocksAuthenticatedState(useLocksAuthStore((state) => state.session));
  const isPaykitConnected = useLocksAuthStore((state) => state.paykitConnected);
  const needsSessionUpgrade = useSessionNeedsUpgrade();

  let step: 'locks' | 'session' | 'bitkit' | 'done' = 'done';
  if (!isLocksAuthenticated) step = 'locks';
  else if (needsSessionUpgrade) step = 'session';
  else if (!isPaykitConnected) step = 'bitkit';

  // Fixed when the dialog opens (adjusted during render, so the first frame already carries it):
  // once the Lock Server step completes only the session step is left, and a count taken then
  // would never show "(2/2)".
  const [isNumbered, setIsNumbered] = useState(() => open && !isLocksAuthenticated && needsSessionUpgrade);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setIsNumbered(!isLocksAuthenticated && needsSessionUpgrade);
  }

  // Probe the Lock Server's readiness when the modal opens; reset when it closes. "Continue" only
  // starts the auth flow once the probe says the server is ready. Read the store here rather than
  // depending on `step`, so opening straight at a later step skips the probe entirely.
  useEffect(() => {
    if (!open) {
      resetLocks();
      resetPaykit();
      return;
    }
    if (!isLocksAuthenticatedState(useLocksAuthStore.getState().session)) prepare();
  }, [open, prepare, resetLocks, resetPaykit]);

  // The Paykit page is the whole step, so it opens on arrival rather than behind another "Continue",
  // once the setup-status check says it is required.
  useEffect(() => {
    if (open && step === 'bitkit' && paykitStatus === PaykitSetupFlowStatus.IDLE) checkPaykit();
  }, [open, step, paykitStatus, checkPaykit]);

  const close = () => onOpenChange(false);
  const isSuccess = step === 'done';
  const isCheckingServer = locksStatus === LocksAuthFlowStatus.CHECKING_SERVER;
  const isServerUnavailable = locksStatus === LocksAuthFlowStatus.SERVER_UNAVAILABLE;
  const isLocksError = step === 'locks' && locksStatus === LocksAuthFlowStatus.ERROR;
  const isPaykitError = step === 'bitkit' && paykitStatus === PaykitSetupFlowStatus.ERROR;
  const isPaykitUnavailable = step === 'bitkit' && paykitStatus === PaykitSetupFlowStatus.UNAVAILABLE;
  const isError = isLocksError || isPaykitError || isPaykitUnavailable;
  // Intro step: the readiness probe (checking / ready / unavailable), before the auth iframe.
  const isIntroPhase =
    step === 'locks' && (isCheckingServer || isServerUnavailable || locksStatus === LocksAuthFlowStatus.IDLE);
  const isEnableStep =
    step === 'locks' &&
    (locksStatus === LocksAuthFlowStatus.CONNECTING ||
      locksStatus === LocksAuthFlowStatus.AWAITING_APPROVAL ||
      locksStatus === LocksAuthFlowStatus.EXCHANGING);
  const isSessionStep = step === 'session';
  const isBitkitStep = step === 'bitkit' && !isPaykitError && !isPaykitUnavailable;

  let title = 'Lock Content';
  let description: ReactNode = 'Pubky Locks allows you to lock content with payments.';
  if (isSuccess) {
    title = 'Locks Enabled';
    description = 'You authorized the Locks server to manage your Locks data.';
  } else if (isEnableStep) {
    title = isNumbered ? 'Enable Locks (1/2)' : 'Enable Locks';
    description = (
      <>
        {'Use '}
        <Link href={getPubkyRingLink()} className="text-base font-bold">
          {'Pubky Ring'}
        </Link>
        {' to authorize Locks server to manage your Locks data.'}
      </>
    );
  } else if (isSessionStep) {
    title = isNumbered ? 'Enable Locks (2/2)' : 'Enable Locks';
    description = <SessionUpgradeDescription />;
  } else if (isBitkitStep) {
    title = 'Enable Payments';
    // No setup URL yet: the setup-status check is running, and there is no QR to scan.
    description = setupUrl ? (
      <>
        {'Scan this QR with your '}
        <Link href={BITKIT_WEBSITE_URL} className="text-base font-bold">
          {'Bitkit'}
        </Link>
        {' wallet to enable payments.'}
      </>
    ) : (
      'Checking whether your Bitkit wallet is connected.'
    );
  } else if (isPaykitError) {
    title = 'Enable Payments';
    description = 'Something went wrong while connecting your Bitkit wallet.';
  } else if (isPaykitUnavailable) {
    title = 'Enable Payments';
    description = 'Something went wrong while checking your Bitkit wallet.';
  } else if (isLocksError) {
    title = 'Enable Locks';
    description = 'Something went wrong while authorizing the Lock Server.';
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent overrideDefaults className={LOCKS_DIALOG_CARD_CLASSNAME} onOpenAutoFocus={focusLocksDialogPanel}>
        <DialogHeader className="gap-6">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription
            className={cn(
              'text-base text-secondary-foreground',
              (isEnableStep || isSessionStep) && LOCKS_DIALOG_DESCRIPTION_TRACKING,
            )}
          >
            {description}
          </DialogDescription>
        </DialogHeader>

        {isIntroPhase && <StepImage src="/images/locks-intro.webp" alt={title} />}

        {isServerUnavailable && (
          <Typography size="sm" className="text-center text-destructive">
            {'The Lock Server is unavailable right now. Please try again later.'}
          </Typography>
        )}

        {isEnableStep && (
          // min-h reserves the iframe's height so the loader states (spinner) don't collapse and
          // then jump when the iframe mounts.
          <Container className="flex flex-col items-center justify-center gap-6 py-3">
            {/* The shell's embed layout is the 192px QR panel, or the deep-link button on touch.
                It also posts a `locks-auth-resize` height we could adopt instead, but a fixed box
                keeps the loader from collapsing before the iframe mounts. */}
            <Container className="flex min-h-48 w-full items-center justify-center pointer-coarse:min-h-15">
              {locksStatus === LocksAuthFlowStatus.AWAITING_APPROVAL && connectUrl ? (
                <iframe
                  ref={locksIframeRef}
                  src={connectUrl}
                  title={'Lock Server authorization'}
                  // The parent accepts the auth code only from the configured Lock Server origin.
                  // Without allow-same-origin, sandboxed postMessage uses origin "null" and gets rejected.
                  // The other flags let /connect run JS, submit its approval form, and open Pubky Ring.
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  className="h-48 w-full pointer-coarse:h-15"
                />
              ) : (
                <LoaderCircle className="size-8 animate-spin text-brand" />
              )}
            </Container>
            {/* The shell hides its own wordmark on desktop, so the embedder carries it (Figma
                "Dialog / Lock / EnableLocks"). */}
            <AppDownload
              logo={{ src: '/images/logo-pubky-ring.svg', alt: 'Pubky Ring', width: 147 }}
              appStoreUrl={getAppStoreLink()}
              playStoreUrl={getPlayStoreLink()}
            />
          </Container>
        )}

        {isSessionStep && <SessionUpgradePanel />}

        {isBitkitStep && (
          <Container className="flex flex-col items-center justify-center gap-6 py-3">
            <Container className="flex min-h-48 w-full items-center justify-center pointer-coarse:min-h-15">
              {setupUrl ? (
                <iframe
                  ref={paykitIframeRef}
                  src={setupUrl}
                  title={'Bitkit payout account setup'}
                  // Same origin rule as the Lock Server iframe above: without allow-same-origin a
                  // sandboxed postMessage arrives as origin "null" and is rejected. allow-popups is
                  // for the Bitkit deep link Paykit's page opens on touch devices.
                  sandbox="allow-scripts allow-same-origin allow-popups"
                  className="h-48 w-full pointer-coarse:h-15"
                />
              ) : (
                <LoaderCircle className="size-8 animate-spin text-brand" />
              )}
            </Container>
            {setupUrl && (
              <AppDownload
                logo={{ src: '/images/bitkit-logo.svg', alt: 'Bitkit', width: 110 }}
                appStoreUrl={BITKIT_APP_STORE_URL}
                playStoreUrl={BITKIT_PLAY_STORE_URL}
              />
            )}
          </Container>
        )}

        {isSuccess && <StepImage src="/images/locks-enabled.webp" alt={title} />}

        {isError && (
          <Typography size="sm" className="text-destructive">
            {isPaykitUnavailable
              ? 'Payments are unavailable right now. Please try again later.'
              : ((isPaykitError ? paykitError?.message : locksError?.message) ?? 'Lock authorization failed.')}
          </Typography>
        )}

        {isIntroPhase && (
          <DialogFooter className="flex-row gap-4">
            <Button variant={ButtonVariant.OUTLINE} size="lg" className="flex-1" onClick={close}>
              {'Cancel'}
            </Button>
            {/* Enabled only once the server is confirmed ready; a spinner shows while probing. */}
            <Button
              variant={ButtonVariant.DEFAULT}
              size="lg"
              className="flex-1 disabled:pointer-events-auto disabled:cursor-not-allowed"
              disabled={locksStatus !== LocksAuthFlowStatus.IDLE}
              onClick={startLocks}
            >
              {isCheckingServer ? <LoaderCircle className="size-5 animate-spin" /> : 'Continue'}
            </Button>
          </DialogFooter>
        )}

        {isSuccess && (
          <DialogFooter className="flex-row gap-4">
            <Button variant={ButtonVariant.OUTLINE} size="lg" className="flex-1" onClick={close}>
              {'Cancel'}
            </Button>
            <Button
              variant={ButtonVariant.DEFAULT}
              size="lg"
              className="flex-1"
              onClick={() => {
                onSuccess();
                close();
              }}
            >
              {'Continue'}
            </Button>
          </DialogFooter>
        )}

        {isError && (
          <DialogFooter className="flex-row gap-4">
            <Button variant={ButtonVariant.OUTLINE} size="lg" className="flex-1" onClick={close}>
              {'Cancel'}
            </Button>
            {/* A failed Paykit setup retries through the check too, so the setup opens only on `setup_required`. */}
            <Button
              variant={ButtonVariant.DEFAULT}
              size="lg"
              className="flex-1"
              onClick={isPaykitUnavailable || isPaykitError ? checkPaykit : startLocks}
            >
              {'Try again'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
